import { Injectable } from '@angular/core';

import { environment } from '../../../environments/environment';

declare global {
  interface ClarityFn {
    (...args: unknown[]): void;
    q?: unknown[][];
  }

  interface Window {
    clarity?: ClarityFn;
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsLoaderService {
  private initialized = false;

  init(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.initGoogleAnalytics(environment.googleAnalyticsId);
    this.initGoogleTagManager(environment.googleTagManagerId);
    this.initClarity(environment.clarityProjectId);
  }

  private initGoogleAnalytics(measurementId: string): void {
    if (!measurementId || this.hasScript(`ga-${measurementId}`)) {
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.gtag =
      window.gtag ||
      ((...args: unknown[]) => {
        window.dataLayer?.push(args);
      });

    window.gtag('js', new Date());
    window.gtag('config', measurementId);

    this.appendScript(
      `ga-${measurementId}`,
      `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
        measurementId
      )}`
    );
  }

  private initGoogleTagManager(containerId: string): void {
    if (!containerId || this.hasScript(`gtm-${containerId}`)) {
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'gtm.js',
      'gtm.start': new Date().getTime(),
    });

    this.appendScript(
      `gtm-${containerId}`,
      `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(
        containerId
      )}`
    );
  }

  private initClarity(projectId: string): void {
    if (!projectId || this.hasScript(`clarity-${projectId}`)) {
      return;
    }

    if (typeof window.clarity !== 'function') {
      const clarity: ClarityFn = (...args: unknown[]) => {
        (clarity.q = clarity.q || []).push(args);
      };
      clarity.q = clarity.q || [];
      window.clarity = clarity;
    }

    this.appendScript(
      `clarity-${projectId}`,
      `https://www.clarity.ms/tag/${encodeURIComponent(projectId)}`
    );
  }

  private appendScript(id: string, src: string): void {
    const script = document.createElement('script');
    script.id = id;
    script.async = true;
    script.src = src;
    document.head.appendChild(script);
  }

  private hasScript(id: string): boolean {
    return !!document.getElementById(id);
  }
}
